import { isTechnologyRequired } from 'domain/shared/career';
import { CANDIDATE_TAGS } from 'domain/shared/newVacancy';
import { type FilterState } from 'framework/ViewTypes/components';
import { FilterCondition } from 'framework/types';
import type {
  SourcingCandidateTag,
  SavedSourcingFilterParameters,
  SourcingFilterKey,
  SourcingFilterOptions,
  SourcingFilterParameters,
  SourcingFilters,
  SourcingSpecializationOption,
} from '../../../types';
import { SourcingSearchMode } from '../../../types';
import { SOURCING_FILTER_KEYS, SOURCING_FILTERS, SUPPORTED_COMPETENCY_LEVELS } from '../constants';
import {
  isCompetencyLevel,
  normalizeLastInteraction,
  normalizeSpecializationId,
  normalizeStricts,
  normalizeStringArray,
  normalizeTechnologyId,
} from './sourcingFilterNormalization';

const getStringValue = (filters: FilterState, key: string): string | undefined => {
  const value = filters[key]?.[FilterCondition.EQ];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const getStringValues = (filters: FilterState, key: string): string[] | undefined => {
  const value = filters[key]?.[FilterCondition.IN];
  return Array.isArray(value) && value.every(item => typeof item === 'string') ? value : undefined;
};

export const mapCollectionFiltersToSourcingFilters = (filters: FilterState): SourcingFilters => {
  const strictFilterKeys = new Set<string>(SOURCING_FILTER_KEYS);
  const candidateTags = new Set<string>(Object.keys(CANDIDATE_TAGS));
  const stricts = getStringValues(filters, SOURCING_FILTERS.stricts)?.filter((key): key is SourcingFilterKey =>
    strictFilterKeys.has(key),
  );
  const levels = getStringValues(filters, SOURCING_FILTERS.levels)?.filter(isCompetencyLevel);
  const skills = getStringValues(filters, SOURCING_FILTERS.skills);
  const location = getStringValues(filters, SOURCING_FILTERS.location);
  const tags = getStringValues(filters, SOURCING_FILTERS.tags)?.filter((tag): tag is SourcingCandidateTag =>
    candidateTags.has(tag),
  );
  const specializationId = normalizeSpecializationId(getStringValue(filters, SOURCING_FILTERS.specialization));
  const technologyId = normalizeTechnologyId(getStringValue(filters, SOURCING_FILTERS.technology));
  const englishLevel = getStringValue(filters, SOURCING_FILTERS.englishLevel);
  const from = getStringValue(filters, `${SOURCING_FILTERS.lastInteraction}.from`);
  const to = getStringValue(filters, `${SOURCING_FILTERS.lastInteraction}.to`);

  return {
    ...(stricts?.length ? { stricts: Array.from(new Set(stricts)) } : {}),
    ...(specializationId !== undefined ? { specializationId } : {}),
    ...(technologyId ? { technologyId } : {}),
    ...(levels?.length ? { levels: Array.from(new Set(levels)) } : {}),
    ...(skills?.length ? { skills: Array.from(new Set(skills)) } : {}),
    ...(englishLevel ? { englishLevel } : {}),
    ...(location?.length ? { location: Array.from(new Set(location)) } : {}),
    ...(tags?.length ? { tags: Array.from(new Set(tags)) } : {}),
    ...(from || to ? { lastInteraction: { from: from ?? null, to: to ?? null } } : {}),
  };
};

export const mapSourcingFiltersToCollectionFilters = (filters: SourcingFilters): FilterState => {
  const collectionFilters: FilterState = {};
  const setEqualsFilter = (key: string, value: string | undefined) => {
    if (value !== undefined && value !== '') {
      collectionFilters[key] = { [FilterCondition.EQ]: value };
    }
  };
  const setInFilter = (key: string, values: readonly string[] | undefined) => {
    if (values?.length) {
      collectionFilters[key] = { [FilterCondition.IN]: [...values] };
    }
  };

  setInFilter(SOURCING_FILTERS.stricts, filters.stricts);
  if (filters.specializationId !== undefined) {
    setEqualsFilter(SOURCING_FILTERS.specialization, String(filters.specializationId));
  }
  setEqualsFilter(SOURCING_FILTERS.technology, filters.technologyId);
  setInFilter(SOURCING_FILTERS.levels, filters.levels);
  setInFilter(SOURCING_FILTERS.skills, filters.skills);
  setEqualsFilter(SOURCING_FILTERS.englishLevel, filters.englishLevel);
  setInFilter(SOURCING_FILTERS.location, filters.location);
  setInFilter(SOURCING_FILTERS.tags, filters.tags);
  setEqualsFilter(`${SOURCING_FILTERS.lastInteraction}.from`, filters.lastInteraction?.from ?? undefined);
  setEqualsFilter(`${SOURCING_FILTERS.lastInteraction}.to`, filters.lastInteraction?.to ?? undefined);

  return collectionFilters;
};

export const normalizeSourcingFilterOptions = (options: SourcingFilterOptions): SourcingFilterOptions => {
  const specializations = new Map<number, SourcingSpecializationOption>();

  options.specializations.forEach(option => {
    const specializationId = normalizeSpecializationId(option.specializationId);
    if (specializationId === undefined) return;

    const specialization = specializations.get(specializationId) ?? {
      ...option,
      specializationId,
      technologies: [],
      levels: [],
    };
    const technologyIds = new Set(specialization.technologies.map(technology => technology.technologyId));

    option.technologies.forEach(technology => {
      const technologyId = normalizeTechnologyId(technology.technologyId);
      if (technologyId === undefined || technologyIds.has(technologyId)) return;

      technologyIds.add(technologyId);
      specialization.technologies.push({ ...technology, technologyId });
    });

    specialization.levels = Array.from(new Set([...specialization.levels, ...option.levels]));
    specializations.set(specializationId, specialization);
  });

  return { ...options, specializations: Array.from(specializations.values()) };
};

export const findSourcingSpecialization = (options: SourcingFilterOptions, value: unknown) => {
  const specializationId = normalizeSpecializationId(value);
  return options.specializations.find(option => option.specializationId === specializationId);
};

const normalizeFilters = (filters: Partial<SourcingFilters>): SourcingFilters => {
  const levels = Array.isArray(filters.levels)
    ? Array.from(
        new Set(filters.levels.filter(level => (SUPPORTED_COMPETENCY_LEVELS as readonly string[]).includes(level))),
      )
    : undefined;
  const populatedLevels = levels?.length ? levels : undefined;
  const skills = normalizeStringArray(filters.skills);
  const location = normalizeStringArray(filters.location);
  const tags = normalizeStringArray(filters.tags);
  const lastInteraction = normalizeLastInteraction(filters);

  return {
    ...(filters.stricts ? { stricts: normalizeStricts(filters.stricts) } : {}),
    ...(populatedLevels ? { levels: populatedLevels } : {}),
    ...(skills ? { skills } : {}),
    ...(typeof filters.englishLevel === 'string' && filters.englishLevel ? { englishLevel: filters.englishLevel } : {}),
    ...(location ? { location } : {}),
    ...(tags ? { tags } : {}),
    ...(lastInteraction ? { lastInteraction } : {}),
  };
};

export const resolveInitialSourcingFilters = (
  filterOptions: SourcingFilterOptions,
  currentFilters: Partial<SourcingFilters>,
): SourcingFilters => {
  const options = normalizeSourcingFilterOptions(filterOptions);
  const filters = normalizeFilters(currentFilters);
  const currentSpecialization = findSourcingSpecialization(options, currentFilters.specializationId);
  const specialization =
    currentSpecialization ?? (options.specializations.length === 1 ? options.specializations[0] : undefined);
  const currentTechnologyId = normalizeTechnologyId(currentFilters.technologyId);
  const currentTechnology = specialization?.technologies.find(option => option.technologyId === currentTechnologyId);
  const technology =
    currentTechnology ?? (specialization?.technologies.length === 1 ? specialization.technologies[0] : undefined);

  return {
    ...filters,
    ...(specialization ? { specializationId: specialization.specializationId } : {}),
    ...(technology ? { technologyId: technology.technologyId } : {}),
  };
};

export const isSourcingSelectionComplete = (
  filterOptions: SourcingFilterOptions,
  filters: Partial<SourcingFilters>,
): boolean => {
  const options = normalizeSourcingFilterOptions(filterOptions);
  const specialization = findSourcingSpecialization(options, filters.specializationId);
  if (!specialization) return false;

  const technologyId = normalizeTechnologyId(filters.technologyId);
  const hasMatchingTechnology = specialization.technologies.some(
    technology => technology.technologyId === technologyId,
  );

  if (technologyId && !hasMatchingTechnology) return false;
  return !isTechnologyRequired(specialization.specializationId) || hasMatchingTechnology;
};

export const shouldOpenInitialSourcingSettings = (
  filterOptions: SourcingFilterOptions,
  filters: Partial<SourcingFilters>,
): boolean => {
  const options = normalizeSourcingFilterOptions(filterOptions);
  const resolvedFilters = resolveInitialSourcingFilters(options, filters);
  if (isSourcingSelectionComplete(options, resolvedFilters)) return false;
  if (options.specializations.length > 1) return true;

  const specialization =
    findSourcingSpecialization(options, resolvedFilters.specializationId) ?? options.specializations[0];
  if (!specialization) return false;

  const hasSelectedTechnology = specialization.technologies.some(
    technology => technology.technologyId === resolvedFilters.technologyId,
  );

  return (
    specialization.technologies.length > 1 ||
    (isTechnologyRequired(specialization.specializationId) && !hasSelectedTechnology)
  );
};

export const getSourcingFilterMode = (
  key: SourcingFilterKey,
  filters: Pick<SourcingFilters, 'stricts'>,
): SourcingSearchMode =>
  key === SOURCING_FILTERS.specialization ||
  key === SOURCING_FILTERS.technology ||
  (filters?.stricts?.includes(key) ?? false)
    ? SourcingSearchMode.STRICT
    : SourcingSearchMode.NON_STRICT;

const getField = <Value>(key: SourcingFilterKey, value: Value | null | undefined, filters: SourcingFilters) =>
  value === undefined || value === null
    ? undefined
    : {
        value,
        mode: getSourcingFilterMode(key, filters),
      };

export const mapSourcingFiltersToParameters = (filters: SourcingFilters): SourcingFilterParameters => {
  const parameters: SourcingFilterParameters = {};
  const specializationId = normalizeSpecializationId(filters.specializationId);
  const technologyId = normalizeTechnologyId(filters.technologyId);

  if (specializationId !== undefined) {
    parameters.specializationId = {
      value: specializationId,
      mode: SourcingSearchMode.STRICT,
    };
  }
  if (technologyId !== undefined) {
    parameters.technologyId = {
      value: technologyId,
      mode: SourcingSearchMode.STRICT,
    };
  }
  if (filters.levels?.length) {
    parameters.levels = getField(SOURCING_FILTERS.levels, filters.levels, filters);
  }
  if (filters.skills?.length) parameters.skills = getField(SOURCING_FILTERS.skills, filters.skills, filters);
  if (filters.englishLevel) {
    parameters.englishLevel = getField(SOURCING_FILTERS.englishLevel, filters.englishLevel, filters);
  }
  if (filters.location?.length) {
    parameters.location = getField(SOURCING_FILTERS.location, filters.location, filters);
  }
  if (filters.tags?.length) parameters.tags = getField(SOURCING_FILTERS.tags, filters.tags, filters);
  if (filters.lastInteraction?.from || filters.lastInteraction?.to) {
    parameters.lastInteraction = getField(SOURCING_FILTERS.lastInteraction, filters.lastInteraction, filters);
  }

  return parameters;
};

export const mapSourcingFiltersToSavedParameters = (
  filters: SourcingFilters,
): SavedSourcingFilterParameters | undefined => {
  const parameters = mapSourcingFiltersToParameters(filters);
  return parameters.specializationId ? { ...parameters, specializationId: parameters.specializationId } : undefined;
};

export const mapSourcingParametersToFilters = (parameters: SourcingFilterParameters): SourcingFilters => {
  const stricts: SourcingFilterKey[] = [];
  const addStrict = (key: SourcingFilterKey, mode?: SourcingSearchMode) => {
    if (mode === SourcingSearchMode.STRICT) stricts.push(key);
  };

  addStrict(SOURCING_FILTERS.levels, parameters.levels?.mode);
  addStrict(SOURCING_FILTERS.skills, parameters.skills?.mode);
  addStrict(SOURCING_FILTERS.englishLevel, parameters.englishLevel?.mode);
  addStrict(SOURCING_FILTERS.location, parameters.location?.mode);
  addStrict(SOURCING_FILTERS.tags, parameters.tags?.mode);
  addStrict(SOURCING_FILTERS.lastInteraction, parameters.lastInteraction?.mode);

  return {
    stricts,
    ...(parameters.specializationId ? { specializationId: parameters.specializationId.value } : {}),
    ...(parameters.technologyId ? { technologyId: parameters.technologyId.value } : {}),
    ...(parameters.levels ? { levels: parameters.levels.value } : {}),
    ...(parameters.skills?.value ? { skills: parameters.skills.value } : {}),
    ...(parameters.englishLevel?.value ? { englishLevel: parameters.englishLevel.value } : {}),
    ...(parameters.location?.value ? { location: parameters.location.value } : {}),
    ...(parameters.tags?.value ? { tags: parameters.tags.value } : {}),
    ...(parameters.lastInteraction?.value ? { lastInteraction: parameters.lastInteraction.value } : {}),
  };
};

export const toggleSourcingFilterMode = (filters: SourcingFilters, key: SourcingFilterKey): SourcingFilters => {
  if (key === SOURCING_FILTERS.specialization || key === SOURCING_FILTERS.technology) return filters;
  const stricts = filters?.stricts?.includes(key)
    ? filters.stricts.filter(strictKey => strictKey !== key)
    : [...(filters?.stricts ?? []), key];
  return { ...filters, stricts };
};
